/* CHI-20: buyer-only tickets for one event (EventDetail tab + registration card). */
import { useTicketingApiWiring } from "@/lib/ticketingApiProvider";
import { useCallback, useEffect, useRef, useState } from "react";
import { getTicketingApi, watchTicketQuote, type MyTicket, type MyTicketsForEvent, type PendingOrder, type TicketQuote } from "@/lib/ticketingApi";
import { onTicketsConfirmed } from "@/lib/ticketsConfirmed";

/**
 * CHI-22 buyer signal for shouldShowBuyTickets: Dev2's hasBlockingTicket (active OR suspended).
 * SEAM: until Dev2's field lands, fall back to "has an active ticket" (suspended is invisible
 * client-side; the server's 409 in ticketCheckout still refuses a second paid order).
 */
export function hasBlockingTicketFrom(res: { tickets?: Pick<MyTicket, "status">[] | null; hasBlockingTicket?: boolean } | null | undefined): boolean {
  if (!res) return false;
  return res.hasBlockingTicket ?? (res.tickets ?? []).some(t => t.status === "active");
}

export function useMyTicketsForEvent(eventSlug: string, enabled: boolean, wantQuote = false) {
  useTicketingApiWiring();
  const [state, setState] = useState<{ status: "idle" | "loading" | "ok" | "error"; tickets: MyTicket[]; pendingOrder: PendingOrder | null; finalizing: boolean; hasBlockingTicket?: boolean; serverNow?: string; viewerTicketing?: MyTicketsForEvent["viewerTicketing"] }>({ status: "idle", tickets: [], pendingOrder: null, finalizing: false });
  const [quote, setQuote] = useState<TicketQuote | null>(null);
  const request = useRef(0);
  const load = useCallback(async () => {
    if (!enabled || !eventSlug) return;
    const current = ++request.current;
    setState(s => ({ ...s, status: s.status === "ok" ? "ok" : "loading" }));
    try {
      const res = await getTicketingApi().myTicketsForEvent({ slug: eventSlug });
      if (current !== request.current) return;
      setState({ status: "ok", tickets: res.tickets ?? [], pendingOrder: res.pendingOrder ?? null, finalizing: res.pendingOrder?.status === "paid", hasBlockingTicket: res.hasBlockingTicket, serverNow: res.serverNow, viewerTicketing: res.viewerTicketing /* CompleteTicketPurchase (temporary) */ });
    } catch {
      if (current !== request.current) return;
      setState(s => ({ ...s, status: "error", tickets: [], finalizing: false }));
    }
  }, [eventSlug, enabled]);
  useEffect(() => {
    if (!enabled) { setState({ status: "idle", tickets: [], pendingOrder: null, finalizing: false }); return; }
    void load();
    return () => { request.current++; };
  }, [enabled, load]);
  // Follow a real pending order through webhook confirmation and QR issuance without a refresh.
  useEffect(() => {
    if (!enabled || !state.pendingOrder) return;
    const refresh = () => { if (document.visibilityState !== "hidden") void load(); };
    const timer = setInterval(refresh, 3000);
    document.addEventListener("visibilitychange", refresh);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [enabled, state.pendingOrder?.orderId, load]);
  // CHI-27: refetch when the return page confirms this event's tickets (tab appears, no refresh).
  useEffect(() => {
    if (!enabled || !eventSlug) return;
    return onTicketsConfirmed(slugs => { if (slugs.includes(eventSlug)) void load(); });
  }, [enabled, eventSlug, load]);
  useEffect(() => {
    if (!wantQuote || !eventSlug) return;
    let live = true;
    const stop = watchTicketQuote(eventSlug, q => { if (live) setQuote(q); }, () => { if (live) setQuote(null); });
    return () => { live = false; stop(); };
  }, [wantQuote, eventSlug]);
  /** Tab visibility (John): ONLY webhook-issued ACTIVE tickets, loaded OK. */
  const activeTickets = state.status === "ok" && !state.finalizing
    ? state.tickets.filter(t => t.status === "active" && typeof t.qrToken === "string" && t.qrToken.length > 0) : [];
  const hasBlockingTicket = state.status === "ok" && hasBlockingTicketFrom(state);
  return { ...state, activeTickets, hasBlockingTicket, showTab: enabled && activeTickets.length > 0, quote, reload: load };
}
